import { useEffect, useState } from "react";
import { Survey, Model } from '@navriti/forms';
import { useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MESSAGES } from '../messages.js/viewerTexts';
import '../../pages/viewer.css';
import { buildSurveyResponseData } from "../../utils/buildSurveyResponseData";
import { bindSurveyFileHandlers, applyFileSourceTypes } from '../../utils/surveyFileHandlers';
import { ensureS3FileQuestions } from "../../utils/ensureS3FileQuestions";
import { useRef } from "react";

export default function PublicFormView() {
  const [json, setJson] = useState(null);
  const [model, setModel] = useState(null);
  const [formId, setFormId] = useState(null);
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const emailBlockedRef = useRef(false);

  useEffect(() => {
  if (!json?.logo) return;
  const interval = setInterval(() => {
    const formEl = document.querySelector('form[class*="_form_"]');
    if (formEl) {
      // Don't add twice
      if (formEl.querySelector('.form-logo-wrapper')) {
        clearInterval(interval);
        return;
      }
      const logoWrapper = document.createElement('div');
      logoWrapper.className = 'form-logo-wrapper';
      logoWrapper.style.cssText = 'text-align:left;padding:0;margin-bottom:-16px;';
      const img = document.createElement('img');
      img.src = json.logo;
      img.alt = 'Form logo';
      img.style.cssText = 'height:48px;width:48px;object-fit:contain;display:block;';
      logoWrapper.appendChild(img);
      formEl.insertBefore(logoWrapper, formEl.firstChild);
      clearInterval(interval);
    }
  }, 300);
  return () => clearInterval(interval);
}, [json]);

  useEffect(() => {
    const load = async () => {
        try {
        const params = new URLSearchParams(location.search);
        const encryptedId = params.get("id");

        const res = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/public/forms/${encodeURIComponent(encryptedId)}`
        );

        if (!res.ok) {
            throw new Error("Form not available");
        }

        const data = await res.json();
        setJson(data.formData);
        const decryptRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/decryptId`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ encryptedId }),
        });
        const { decryptedId } = await decryptRes.json();
        setFormId(decryptedId);
        } catch (e) {
            console.error(e);
            toast.error(MESSAGES.FETCH_FORM_FAIL || "Form not available");
        }
    };

    load();
    }, [location.search]);

const handleComplete = async (sender) => {
  try {
    const params = new URLSearchParams(location.search);
    const encryptedId = params.get("id");

    // decrypt on BACKEND, not frontend
    const decryptRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/decryptId`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedId }),
      }
    );

    const { decryptedId } = await decryptRes.json();

    const responseData = buildSurveyResponseData(sender);
    const saveRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/ResponseSave`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formId: decryptedId,
          data: responseData,
        }),
      }
    );

    if (!saveRes.ok) {
      throw new Error("Failed to save response");
    }
    setSubmitted(true);
    toast.success(MESSAGES.SAVE_RESPONSE_SUCCESS);
  } catch (err) {
    console.error(err);
    toast.error(MESSAGES.SAVE_RESPONSE_FAIL || "Failed to submit response");
  }
};

useEffect(() => {
  if (!json) {
    setModel(null);
    return;
  }

  const params = new URLSearchParams(location.search);
  const encryptedFormId = params.get("id");
  const formJson = structuredClone(json);
  if (formJson.oneResponsePerEmail) {
    const emailQuestion = {
      type: "text",
      name: "__submitter_email__",
      title: "Please enter your email address",
      isRequired: true,
      inputType: "email",
      validators: [{ type: "email" }],
    };
    if (formJson.pages && formJson.pages.length > 0) {
      formJson.pages[0].elements = [
        emailQuestion,
        ...(formJson.pages[0].elements || []),
      ];
    }
  }
const surveyModel = new Model(ensureS3FileQuestions(formJson));
  surveyModel.onAfterRenderQuestion?.add((survey, options) => {
    if (options.question.getType() === "file") {
      options.question.pageSize = options.question.value?.length || 10;
    }
  });
  bindSurveyFileHandlers(surveyModel, { encryptedFormId, formJson });
  applyFileSourceTypes(formJson);
  if (formJson.oneResponsePerEmail) {
  // DOM-based email check since onValueChanged is not supported in compat layer
  setTimeout(() => {
    const emailInput = document.querySelectorAll('input')[0];
    if (!emailInput) return;

    let emailCheckTimeout = null;

    emailInput.addEventListener('input', (e) => {
      const email = e.target.value;
      if (emailCheckTimeout) clearTimeout(emailCheckTimeout);
      if (!email || !formId) return;

      emailCheckTimeout = setTimeout(async () => {
        try {
          const checkRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/forms/${formId}/check-email`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email }),
            }
          );
          const checkData = await checkRes.json();

          let errorEl = emailInput.parentElement.querySelector('.email-dup-error');
          emailCheckTimeout = setTimeout(async () => {
  try {
    const checkRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/forms/${formId}/check-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    const checkData = await checkRes.json();

    let errorEl = emailInput.parentElement.querySelector('.email-dup-error');
    if (!checkData.allowed) {
      emailBlockedRef.current = true;
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'email-dup-error';
        errorEl.style.cssText = 'color:#e03131;font-size:12px;margin-top:4px;';
        emailInput.parentElement.appendChild(errorEl);
      }
      errorEl.textContent = checkData.message || "You have already submitted this form with this email.";
      document.querySelectorAll('input, select, textarea').forEach((el) => {
        if (el === emailInput) return;
        el.disabled = true;
        el.style.opacity = '0.5';
      });

      const submitBtn = Array.from(document.querySelectorAll('button')).find(
        (btn) => btn.textContent.trim().toLowerCase() === 'submit'
      );
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.style.opacity = '0.5';
        submitBtn.style.cursor = 'not-allowed';
        submitBtn.style.pointerEvents = 'none';
      }
    } else {
      emailBlockedRef.current = false;
      if (errorEl) errorEl.remove();
      document.querySelectorAll('input, select, textarea').forEach((el) => {
        if (el === emailInput) return;
        el.disabled = false;
        el.style.opacity = '1';
      });;

      const submitBtn = Array.from(document.querySelectorAll('button')).find(
        (btn) => btn.textContent.trim().toLowerCase() === 'submit'
      );
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.style.opacity = '1';
        submitBtn.style.cursor = 'pointer';
        submitBtn.style.pointerEvents = 'auto';
      }
    }
  } catch (err) {
    console.error("Email check failed:", err);
  }
}, 300);
        } catch (err) {
          console.error("Email check failed:", err);
        }
      }, 300);
    });
  }, 1000);
}
  surveyModel.onComplete?.add((s) => {
    console.log("onComplete fired, blocked:", emailBlockedRef.current);
    if (emailBlockedRef.current) {
      toast.error("You have already submitted this form with this email.");
      return;  // stop here — don't call setSubmitted or handleComplete
    }
    setSubmitted(true);
    handleComplete(s);
  });
  setModel(surveyModel);
}, [json, location.search, formId]);

if (submitted) {
  return (
    <div style={{ textAlign: 'center', marginTop: '100px' }}>
      <ToastContainer />
      <h2 style={{ color: '#17a387', fontSize: '28px' }}>Thank you for completing the survey!</h2>
      <p style={{ color: '#666', fontSize: '16px', marginTop: '12px' }}>Your response has been saved successfully.</p>
    </div>
  );
}

if (!model) {
  return (
    <>
      <ToastContainer />
      <p style={{ color: "gray", fontSize: "25px", textAlign: "center", marginTop: "20px" }}>
        Loading...
      </p>
    </>
  );
}

  return (
    <>
        <ToastContainer />
        {!submitted && <Survey model={model} />}
    </>
    );
}
